/** Locate scannable summary highlights (mirrors shared.text.highlights). */

const SPACE_CHARS = new Set([" ", "\t", "\n", "\r", "\u00A0", "\u2009", "\u202F"]);
const EDGE_STRIP_CHARS = new Set([
  ...SPACE_CHARS,
  '"',
  "'",
  "\u201C",
  "\u201D",
  "\u00AB",
  "\u00BB",
  "\u2018",
  "\u2019",
  "\u2039",
  "\u203A",
  ".",
  ",",
  ";",
  ":",
]);
const QUOTE_MAP: Record<string, string> = {
  "\u201C": '"',
  "\u201D": '"',
  "\u00AB": '"',
  "\u00BB": '"',
  "\u2018": "'",
  "\u2019": "'",
  "\u2039": "'",
  "\u203A": "'",
};
const DASH_MAP: Record<string, string> = {
  "\u2014": "-",
  "\u2013": "-",
  "\u2212": "-",
};

export const MIN_HIGHLIGHT_WORDS = 2;
export const MAX_HIGHLIGHT_WORDS = 10;

export type DiscardReason =
  | "empty"
  | "too_short"
  | "too_long"
  | "bad_index"
  | "duplicate_index"
  | "not_found"
  | "ambiguous"
  | "mid_word"
  | "repeats_title"
  | "parse_failed"
  | "missing";

export interface SummaryHighlight {
  p: number;
  text: string;
}

export interface HighlightLocateResult {
  ok: boolean;
  start?: number;
  end?: number;
  painted?: string;
  reason?: DiscardReason;
}

const SPLIT_RE = /\n(?:[ \t]*\n)+/;
const WORD_CHAR_RE = /[\p{L}\p{N}]/u;

export function splitSummaryParagraphs(text: string): string[] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const trimmed = trimEdges(normalized);
  if (!trimmed) return [];
  return trimmed
    .split(SPLIT_RE)
    .map((part) => trimEdges(part))
    .filter(Boolean);
}

export function locateHighlight(
  paragraph: string,
  text: string,
): HighlightLocateResult {
  const phrase = stripPhraseEdges(text);
  if (!phrase) return { ok: false, reason: "empty" };

  const { normalized: phraseNorm } = normalizeWithMap(phrase);
  const wordCount = countWords(phraseNorm);
  if (wordCount < MIN_HIGHLIGHT_WORDS) return { ok: false, reason: "too_short" };
  if (wordCount > MAX_HIGHLIGHT_WORDS) return { ok: false, reason: "too_long" };

  const { normalized: paraNorm, map } = normalizeWithMap(paragraph);
  if (!phraseNorm) return { ok: false, reason: "empty" };

  const starts = findAll(paraNorm, phraseNorm);
  if (starts.length === 0) return { ok: false, reason: "not_found" };
  if (starts.length > 1) return { ok: false, reason: "ambiguous" };

  const normStart = starts[0];
  const normEnd = normStart + phraseNorm.length;
  const { start, end } = projectSpan(map, normStart, normEnd, paragraph.length);
  if (!hasWordBoundaries(paragraph, start, end)) {
    return { ok: false, reason: "mid_word" };
  }

  return {
    ok: true,
    start,
    end,
    painted: paragraph.slice(start, end),
  };
}

function trimEdges(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && SPACE_CHARS.has(text[start])) start += 1;
  while (end > start && SPACE_CHARS.has(text[end - 1])) end -= 1;
  return text.slice(start, end);
}

function stripPhraseEdges(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && EDGE_STRIP_CHARS.has(text[start])) start += 1;
  while (end > start && EDGE_STRIP_CHARS.has(text[end - 1])) end -= 1;
  return text.slice(start, end);
}

function normalizeWithMap(text: string): { normalized: string; map: number[] } {
  const out: string[] = [];
  const map: number[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (SPACE_CHARS.has(ch)) {
      const runStart = i;
      while (i < text.length && SPACE_CHARS.has(text[i])) i += 1;
      out.push(" ");
      map.push(runStart);
      continue;
    }
    if (QUOTE_MAP[ch] !== undefined) {
      out.push(QUOTE_MAP[ch]);
      map.push(i);
      i += 1;
      continue;
    }
    if (DASH_MAP[ch] !== undefined) {
      out.push(DASH_MAP[ch]);
      map.push(i);
      i += 1;
      continue;
    }
    out.push(ch);
    map.push(i);
    i += 1;
  }
  return { normalized: out.join(""), map };
}

function countWords(normalized: string): number {
  const trimmed = normalized.replace(/^ +| +$/g, "");
  if (!trimmed) return 0;
  return trimmed.split(" ").filter(Boolean).length;
}

function findAll(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const starts: number[] = [];
  let start = 0;
  while (true) {
    const idx = haystack.indexOf(needle, start);
    if (idx === -1) return starts;
    starts.push(idx);
    start = idx + 1;
  }
}

function projectSpan(
  map: number[],
  normStart: number,
  normEnd: number,
  originalLen: number,
): { start: number; end: number } {
  const start = map[normStart];
  const end = normEnd < map.length ? map[normEnd] : originalLen;
  return { start, end };
}

function isWordChar(ch: string): boolean {
  return WORD_CHAR_RE.test(ch);
}

function hasWordBoundaries(paragraph: string, start: number, end: number): boolean {
  if (start > 0 && isWordChar(paragraph[start - 1])) return false;
  if (end < paragraph.length && isWordChar(paragraph[end])) return false;
  return true;
}
